import React, { useEffect, useState } from "react";
import { tasksAPI } from "../../api/client";
import { BarList, ColumnChart, Meter } from "./DashboardCharts";
import { apiError, formatDueDate, formatMinutes, initials } from "./taskUtils";
import { parseLocalDate } from "../../utils/dateUtils";

function WarningIcon() {
  return (
    <svg className="tk-status-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l9.5 17h-19z" />
      <path d="M12 10v4M12 17.5v.01" className="tk-status-icon-mark" />
    </svg>
  );
}

function StatTile({ label, value, children, tone }) {
  return (
    <div className={`tk-stat ${tone ? `tk-stat-${tone}` : ""}`.trim()}>
      <span className="tk-stat-label">{label}</span>
      <span className="tk-stat-value">{value}</span>
      {children && <span className="tk-stat-sub">{children}</span>}
    </div>
  );
}

function Delta({ current, previous }) {
  const diff = current - previous;
  if (diff === 0)
    return <span className="tk-muted">Same as previous 7 days</span>;
  return (
    <span className={diff > 0 ? "tk-delta-up" : "tk-delta-down"}>
      {diff > 0 ? "▲" : "▼"} {Math.abs(diff)} vs previous 7 days
    </span>
  );
}

function TaskMiniList({ tasks, empty, onOpen, overdue }) {
  if (tasks.length === 0) return <p className="tk-muted">{empty}</p>;
  return (
    <ul className="tk-mini-list">
      {tasks.map((t) => (
        <li key={t.id}>
          <button type="button" onClick={() => onOpen(t.id)}>
            <span className="tk-mini-name">{t.name}</span>
            <span className={`tk-due ${overdue ? "due-overdue" : ""}`}>
              {formatDueDate(t.dueDate)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function formatDay(key, long) {
  const date = parseLocalDate(key);
  return date.toLocaleDateString(
    "en-IN",
    long
      ? { weekday: "short", day: "numeric", month: "short" }
      : { day: "numeric", month: "short" },
  );
}

export default function TaskDashboard({ onOpenTask, onShowTasks }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    tasksAPI
      .dashboard()
      .then((resp) => setData(resp.data))
      .catch((err) => setError(apiError(err, "Could not load dashboard")));
  }, []);

  if (error) return <p className="tk-error">{error}</p>;
  if (!data) return <p className="tk-muted tk-list-msg">Loading dashboard…</p>;

  const { totals, dueBuckets, delegated } = data;
  const completionPct =
    totals.completionRate === null
      ? "—"
      : `${Math.round(totals.completionRate * 100)}%`;

  const dueRows = [
    {
      key: "overdue",
      label: "Overdue",
      value: dueBuckets.overdue,
      icon: dueBuckets.overdue > 0 ? <WarningIcon /> : null,
    },
    { key: "today", label: "Due today", value: dueBuckets.today },
    { key: "thisWeek", label: "Next 6 days", value: dueBuckets.thisWeek },
    { key: "later", label: "Later", value: dueBuckets.later },
    { key: "none", label: "No due date", value: dueBuckets.none },
  ];

  return (
    <div className="tk-dash">
      <section className="tk-dash-kpis" aria-label="Task summary">
        <div className="tk-stat tk-stat-hero">
          <span className="tk-stat-label">Open tasks assigned to you</span>
          <span className="tk-hero-value">{totals.open}</span>
          <span className="tk-stat-sub">
            {totals.assigned} assigned in total ·{" "}
            <button type="button" className="tk-link-btn" onClick={onShowTasks}>
              View tasks
            </button>
          </span>
        </div>
        <StatTile
          label="Overdue"
          value={
            <>
              {totals.overdue > 0 && <WarningIcon />}
              {totals.overdue}
            </>
          }
          tone={totals.overdue > 0 ? "critical" : null}
        >
          {totals.overdue > 0 ? "Past their due date" : "Nothing overdue"}
        </StatTile>
        <StatTile label="Due today" value={totals.dueToday} />
        <StatTile label="Due in the next 7 days" value={totals.dueThisWeek} />
        <StatTile
          label="Completed in the last 7 days"
          value={totals.completedLast7}
        >
          <Delta
            current={totals.completedLast7}
            previous={totals.completedPrev7}
          />
        </StatTile>
        <StatTile label="Completion rate" value={completionPct}>
          <Meter value={totals.completionRate} label="Completion rate" />
          <span className="tk-muted">
            {totals.completed} of {totals.assigned} done
          </span>
        </StatTile>
        <StatTile
          label="Time logged on tasks"
          value={formatMinutes(totals.minutesLoggedLast7)}
        >
          Last 7 days, by you
        </StatTile>
        <StatTile
          label="Waiting on your approval"
          value={totals.approvalsPending}
          tone={totals.approvalsPending > 0 ? "attention" : null}
        >
          {totals.approvalsPending > 0
            ? "Approvals assigned to you"
            : "No approvals pending"}
        </StatTile>
        <StatTile label="Waiting on other tasks" value={totals.waitingOn}>
          Open tasks with unfinished dependencies
        </StatTile>
        <StatTile label="Collaborating on" value={totals.collaboratingOpen}>
          Open tasks others own
        </StatTile>
      </section>

      <div className="tk-dash-grid">
        <section className="tk-card tk-card-wide">
          <h3>Tasks completed per day</h3>
          <p className="tk-muted">Your assigned tasks, last 14 days</p>
          <ColumnChart
            data={data.completedByDay.map((d) => ({
              key: d.date,
              value: d.count,
            }))}
            valueLabel="completed"
            formatX={formatDay}
            ariaLabel="Tasks completed per day over the last 14 days"
          />
        </section>

        <section className="tk-card">
          <h3>Open tasks by due date</h3>
          <p className="tk-muted">{totals.open} open tasks</p>
          <BarList rows={dueRows} total={totals.open} />
        </section>

        <section className="tk-card">
          <h3>Open tasks by project</h3>
          <p className="tk-muted">A task in two projects counts in both</p>
          <BarList
            rows={data.byProject.map((p) => ({
              key: p.id,
              label: p.name,
              value: p.count,
            }))}
            total={totals.open}
          />
        </section>

        <section className="tk-card">
          <h3>
            {dueBuckets.overdue > 0 && <WarningIcon />}
            Overdue
          </h3>
          <TaskMiniList
            tasks={data.overdueTasks}
            empty="You're on track — nothing overdue."
            onOpen={onOpenTask}
            overdue
          />
        </section>

        <section className="tk-card">
          <h3>Coming up</h3>
          <TaskMiniList
            tasks={data.upcomingTasks}
            empty="No upcoming due dates."
            onOpen={onOpenTask}
          />
        </section>

        <section className="tk-card tk-card-wide">
          <h3>Tasks you've assigned to others</h3>
          <p className="tk-muted">
            {delegated.open} open · {delegated.overdue} overdue ·{" "}
            {delegated.completed} completed
          </p>
          {delegated.byPerson.length === 0 ? (
            <p className="tk-muted">
              Tasks you create and assign to teammates are tracked here.
            </p>
          ) : (
            <table className="tk-viz-table tk-people-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Open</th>
                  <th>Overdue</th>
                  <th>Completed</th>
                </tr>
              </thead>
              <tbody>
                {delegated.byPerson.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <span className="tk-assignee">
                        <span className="tk-avatar tk-avatar-sm">
                          {initials(p.name)}
                        </span>
                        {p.name}
                      </span>
                    </td>
                    <td>{p.open}</td>
                    <td className={p.overdue > 0 ? "tk-cell-critical" : ""}>
                      {p.overdue > 0 && <WarningIcon />}
                      {p.overdue}
                    </td>
                    <td>{p.completed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  );
}
